/**
 * 浏览器检测工具
 * 用于识别不同的浏览器类型和版本
 */

import i18n from '../i18n';

export interface IBrowserInfo {
  name: string;
  version: string;
  isMobile: boolean;
  isBlocked: boolean; // 是否被完全禁止使用
  shouldSuggest: boolean; // 是否应该建议使用Chrome
}

/**
 * 检测当前浏览器信息
 */
export function detectBrowser(): IBrowserInfo {
  const ua = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  let name = 'Unknown';
  let version = '';
  let isBlocked = false;
  let shouldSuggest = false;

  // 检测360浏览器
  if (ua.indexOf('360SE') > -1 || ua.indexOf('360EE') > -1) {
    name = '360Browser';
    isBlocked = true;
  }
  // 检测QQ浏览器
  else if (ua.indexOf('QQBrowser') > -1 || ua.indexOf('MQQBrowser') > -1) {
    name = 'QQBrowser';
    isBlocked = true;
  }
  // 检测搜狗浏览器
  else if (ua.indexOf('MetaSr') > -1 || ua.indexOf('Sogou') > -1) {
    name = 'SogouBrowser';
    isBlocked = true;
  }
  // 检测UC浏览器
  else if (ua.indexOf('UCBrowser') > -1 || ua.indexOf('UCWEB') > -1) {
    name = 'UCBrowser';
    isBlocked = true;
  }
  // 检测百度浏览器
  else if (ua.indexOf('BIDUBrowser') > -1 || ua.indexOf('baidubrowser') > -1) {
    name = 'BaiduBrowser';
    isBlocked = true;
  }
  // 检测2345浏览器
  else if (ua.indexOf('2345Explorer') > -1) {
    name = '2345Browser';
    isBlocked = true;
  }
  // 检测猎豹浏览器
  else if (ua.indexOf('LBBROWSER') > -1 || ua.indexOf('LieBao') > -1) {
    name = 'LieBaoBrowser';
    isBlocked = true;
  }
  // 检测IE浏览器
  else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident/') > -1) {
    name = 'IE';
    isBlocked = true;
    const match = ua.match(/(?:MSIE |rv:)(\d+)/);
    if (match) {
      version = match[1];
    }
  }
  // 微信内置浏览器
  else if (ua.indexOf('MicroMessenger') > -1) {
    name = 'WeChat';
    const match = ua.match(/MicroMessenger\/(\d+)/);
    if (match) {
      version = match[1];
    }
    isBlocked = true; // 微信内置浏览器直接阻止访问
    shouldSuggest = false;
  }
  // 检测主流现代浏览器（Chrome、Edge、Safari、Firefox、Opera等）
  else if (ua.indexOf('Edg') > -1) {
    // Edge浏览器（必须在Chrome之前检测）
    name = 'Edge';
    const match = ua.match(/Edg\/(\d+)/);
    if (match) {
      version = match[1];
    }
  } else if (ua.indexOf('Chrome') > -1) {
    // Chrome浏览器
    name = 'Chrome';
    const match = ua.match(/Chrome\/(\d+)/);
    if (match) {
      version = match[1];
    }
  } else if (ua.indexOf('Safari') > -1) {
    // Safari浏览器（必须在最后检测，因为其他浏览器也可能包含Safari字符串）
    name = 'Safari';
    const match = ua.match(/Version\/(\d+)/);
    if (match) {
      version = match[1];
    }
  }
  // 未知浏览器建议使用Chrome
  if (name === 'Unknown') {
    shouldSuggest = true;
  }
  return {
    name,
    version,
    isMobile,
    isBlocked,
    shouldSuggest
  };
}

/**
 * 获取Chrome下载链接（根据 i18n 当前语言选择合适的地址）
 */
export function getChromeDownloadUrl(isMobile: boolean): string {
  // 默认下载链接配置（作为 fallback）
  const defaultUrls = {
    desktop: 'https://www.google.com/chrome/',
    android: 'https://www.google.com/chrome/',
    ios: 'https://apps.apple.com/app/google-chrome/id535886823'
  };

  // 从 i18n 获取当前语言的下载链接配置
  let languageUrls = defaultUrls;
  try {
    const urls = i18n.t('browserWarning.detector.chromeDownloadUrls', { returnObjects: true });
    if (urls && typeof urls === 'object' && 'desktop' in urls && 'android' in urls && 'ios' in urls) {
      languageUrls = urls as typeof defaultUrls;
    }
  } catch (e) {
    // 如果 i18n 未初始化或获取失败，使用默认值
  }

  if (isMobile) {
    // 移动端
    const ua = navigator.userAgent;
    if (/Android/i.test(ua)) {
      // Android设备
      return languageUrls.android;
    } else if (/iPhone|iPad|iPod/i.test(ua)) {
      // iOS设备
      return languageUrls.ios;
    }
  }

  // 桌面端
  return languageUrls.desktop;
}
